import { checkPermissionByRoles } from "../utils/checkPermission";
import { getNextAvailableTab } from "../utils/getNextAvailableTab";
import { storeUser } from "../redux/users/usersSlice";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import React, { useRef, useState } from "react";
import moment from "moment-timezone";
import ProviderAuthLayout from "../components/auth/ProviderAuthLayout";

const Login = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const emailRef = useRef();
  const navigate = useNavigate();
  const passwordRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const email = emailRef.current.value.trim();
    const password = passwordRef.current.value.trim();

    if (!email || !password) {
      setErrorMessage("Email and password are required.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/login`,
        { email, password }
      );
      if (response.data.success === true) {
        localStorage.setItem("businessId", response?.data?.businessId);
        localStorage.setItem(
          "businessIdentifier",
          response?.data?.businessIdentifier
        );
        localStorage.setItem("isFirstLogin", response?.data?.is_first_login);
        localStorage.setItem(
          "permission",
          JSON.stringify(response?.data?.permission)
        );
        localStorage.setItem("role", JSON.stringify(response?.data?.role));
        localStorage.setItem("user_id", response.data.token);
        localStorage.setItem("username", response.data.username);

        dispatch(
          storeUser({
            user: response?.data,
          })
        );

        let tab = null;

        if (response.data?.role === "teamMember") {
          tab = getNextAvailableTab(
            {
              permission: JSON.stringify(response?.data?.permission),
              token: response?.data?.token,
              user: response?.data,
            },
            checkPermissionByRoles
          );
        }

        let selectedRoute = "dashboard";

        const isBypassRole =
          response.data?.role === "careManager" ||
          response.data?.role === "psychiatricConsultant" ||
          response.data?.providerType === "Care Manager" ||
          response.data?.providerType === "Psychiatric Consultant";

        if (response.data?.role === "provider" || isBypassRole) {
          const {
            providerFirstName,
            providerLastName,
            providerCredentials,
            providerYearsOfExperience,
            providerProfilePhoto,
            providerSpecialisation,
            providerVideoIntroUrl,
            time_zone,
          } = response.data;

          let step = "";

          const newTimeZone = moment.tz.guess();

          if (time_zone && newTimeZone && time_zone !== newTimeZone) {
            try {
              await axios.put(
                `${process.env.REACT_APP_BASE_URL}/specialist/update-timezone`,
                { time_zone: newTimeZone },
                {
                  headers: {
                    Authorization: `Bearer ${response.data.token}`,
                  },
                }
              );
            } catch (tzErr) {
              console.error("Failed to update timezone:", tzErr);
            }
          }

          if (response.data?.role === "provider" && !isBypassRole) {
            if (
              response.data?.providerStatus === "Incomplete" ||
              response.data?.providerStatus === "Invited"
            ) {
              selectedRoute = "dashboard";
            } else if (
              response.data?.providerStatus === "Active" ||
              response.data?.providerStatus === "Pending" ||
              response.data?.providerStatus === "Pending Review"
            ) {
              selectedRoute = "dashboard";
            } else if (
              !providerFirstName ||
              !providerLastName ||
              !(Array.isArray(providerCredentials)
                ? providerCredentials.length > 0
                : providerCredentials) ||
              !providerYearsOfExperience
            ) {
              selectedRoute = "dashboard";
              step = "0";
            } else if (!providerProfilePhoto) {
              selectedRoute = "dashboard";
              step = "1";
            } else if (
              !providerSpecialisation ||
              providerSpecialisation.length === 0
            ) {
              selectedRoute = "dashboard";
              step = "2";
            } else if (!providerVideoIntroUrl) {
              selectedRoute = "dashboard";
              step = "3";
            } else {
              selectedRoute = "appointments";
            }
            if (step) localStorage.setItem("providerStep", step);
          } else if (
            response.data?.role === "careManager" ||
            response.data?.providerType === "Care Manager"
          ) {
            selectedRoute = "dashboard";
          } else {
            selectedRoute = "appointments";
          }
        } else if (response.data?.role === "teamMember") {
          selectedRoute = tab ? tab : "dashboard";
          navigate(`/${selectedRoute}`);
        }

        if (response.data.is_first_login) {
          setTimeout(() => {
            navigate("/update-password");
          }, 1000);
        } else {
          setTimeout(() => {
            navigate(`/${selectedRoute}`);
          }, 1000);
        }
      } else {
        setErrorMessage("Invalid username or password. Please try again.");
      }
    } catch (error) {
      console.error("🚀 -> handleSubmit -> error:", error);

      if (!navigator.onLine) {
        setErrorMessage(
          "Network connection lost. Please check your connection and try again."
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "An error occurred. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProviderAuthLayout>
      <h1 className="provider-auth-title">Sign in</h1>
      <p className="provider-auth-subtitle">
        Sign in to access your Numente account.
      </p>

      <form className="provider-auth-form" onSubmit={handleSubmit}>
        <div className="provider-auth-field">
          <label className="provider-auth-label" htmlFor="provider-login-email">
            Email
          </label>
          <input
            id="provider-login-email"
            className="provider-auth-input form-control"
            placeholder="name@organization.com"
            ref={emailRef}
            type="email"
            autoComplete="email"
          />
        </div>

        <div className="provider-auth-field">
          <div className="provider-auth-label-row">
            <label
              className="provider-auth-label"
              htmlFor="provider-login-password"
            >
              Password
            </label>
            <Link className="provider-auth-link" to="/forgot-password">
              Forgot password?
            </Link>
          </div>
          <div className="provider-auth-password-wrap">
            <input
              id="provider-login-password"
              className="provider-auth-input form-control"
              placeholder="••••••••"
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="provider-auth-eye"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
        </div>

        {errorMessage ? (
          <p className="provider-auth-error" role="alert">
            {errorMessage}
          </p>
        ) : null}

        <button className="provider-auth-btn" type="submit" disabled={loading}>
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>

      <p className="provider-auth-footer">
        Need assistance?{" "}
        <a className="provider-auth-link" href="mailto:support@numente.io">
          Contact Support
        </a>
      </p>
    </ProviderAuthLayout>
  );
};

export default Login;
